import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User, LogOut, LayoutDashboard } from "lucide-react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";

const UserProfileDropdown = () => {
  const { user, profile, signOut } = useAuth(); // Use signOut from context
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut(); // Use the improved signOut function
      navigate("/");
    } catch (error: any) {
      console.error('Error during logout:', error);
      // signOut already handles error messages, so we just navigate
      navigate("/");
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || "U";
  };

  const getProfilePath = () => {
    if (user?.email === "psy@psyonline.clinic") {
      return "/admin";
    }
    if (profile?.role === "psychologist") {
      return "/psychologist-profile";
    }
    return "/patient-profile";
  };

  const getDashboardPath = () => {
    if (user?.email === "psy@psyonline.clinic") {
      return "/admin";
    }
    if (profile?.role === "psychologist") {
      return "/psychologist-dashboard";
    }
    return "/patient-dashboard";
  };

  const getDashboardLabel = () => {
    if (user?.email === "psy@psyonline.clinic") {
      return "Painel Admin";
    }
    if (profile?.role === "psychologist") {
      return "Dashboard Profissional";
    }
    return "Meu Painel";
  };

  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center space-x-2 p-2">
          <Avatar className="h-8 w-8">
            <AvatarImage src={profile?.avatar_url} alt={profile?.first_name} />
            <AvatarFallback className="text-xs">
              {getInitials(profile?.first_name || "", profile?.last_name || "")}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium hidden md:block">
            {profile?.first_name || user.email}
          </span>
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent className="w-56" align="end">
        <DropdownMenuItem asChild>
          <Link to={getDashboardPath()} className="flex items-center">
            <LayoutDashboard className="mr-2 h-4 w-4" />
            <span>{getDashboardLabel()}</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link to={getProfilePath()} className="flex items-center">
            <User className="mr-2 h-4 w-4" />
            <span>Meu Perfil</span>
          </Link>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem onClick={handleSignOut} className="text-red-600">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Sair</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserProfileDropdown;
